import { useState } from 'react';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlay } from '@fortawesome/free-solid-svg-icons';

import type { ILandingPageCruiseLine, ILandingPageRenderData } from '@share/common-types';

import styles from './cruise-lines-section.module.scss';

function hasCruiseProduct(data: ILandingPageRenderData): boolean {
  return (data.products ?? []).some((product) => product.type.toLowerCase() === 'cruise');
}

const MAX_CRUISE_LINE_CONFIGS = 2;

function linesWithMedia(data: ILandingPageRenderData): ILandingPageCruiseLine[] {
  const withMedia = (data.eligibleCruiseLines ?? []).filter(
    (line) => Boolean(line.logoUrl) || Boolean(line.videoEmbedUrl),
  );
  const withLogo = withMedia.filter((line) => line.logoUrl);
  const withoutLogo = withMedia.filter((line) => !line.logoUrl);
  return [...withLogo, ...withoutLogo].slice(0, MAX_CRUISE_LINE_CONFIGS);
}

function youtubeIdFromEmbed(embedUrl?: string): string | null {
  if (!embedUrl) {
    return null;
  }
  const match = embedUrl.match(/youtube(?:-nocookie)?\.com\/embed\/([A-Za-z0-9_-]{6,})/i);
  return match?.[1] ?? null;
}

function CruiseLineVideo({ line }: { line: ILandingPageCruiseLine }): JSX.Element {
  const [playing, setPlaying] = useState(false);
  const youtubeId = youtubeIdFromEmbed(line.videoEmbedUrl);
  const watchUrl =
    line.videoUrl ||
    (youtubeId ? `https://www.youtube.com/watch?v=${youtubeId}` : line.videoEmbedUrl);
  const poster = youtubeId ? `https://img.youtube.com/vi/${youtubeId}/hqdefault.jpg` : undefined;
  const embedSrc = line.videoEmbedUrl?.includes('?')
    ? `${line.videoEmbedUrl}&rel=0`
    : `${line.videoEmbedUrl}?rel=0`;

  return (
    <div className={styles.video}>
      <div className={styles.videoFrame}>
        {playing ? (
          <iframe
            src={embedSrc}
            title={`${line.name} video`}
            className={styles.videoFrameFill}
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
            referrerPolicy="origin"
          />
        ) : (
          <button
            type="button"
            className={styles.playHit}
            onClick={() => setPlaying(true)}
            aria-label={`Play ${line.name} video`}
          >
            {poster ? (
              <img
                src={poster}
                alt=""
                className={`${styles.videoFrameFill} ${styles.poster}`}
              />
            ) : (
              <div className={`${styles.videoFrameFill} ${styles.posterFallback}`} />
            )}
            <span className={styles.overlay} />
            <span className={styles.playButton}>
              <FontAwesomeIcon icon={faPlay} className={styles.playIcon} aria-hidden />
            </span>
          </button>
        )}
      </div>
      {watchUrl ? (
        <a
          href={watchUrl}
          target="_blank"
          rel="noopener noreferrer"
          className={styles.watchLink}
        >
          Watch {line.name} on YouTube
        </a>
      ) : null}
    </div>
  );
}

export function CruiseLinesSection({ data }: { data: ILandingPageRenderData }): JSX.Element | null {
  if (!hasCruiseProduct(data)) return null;

  const lines = linesWithMedia(data);
  if (lines.length === 0) return null;

  const logos = lines.filter((line) => line.logoUrl);
  const videos = lines.filter((line) => line.videoEmbedUrl);

  return (
    <section className={styles.section}>
      <div className="container">
        <div className={styles.header}>
          <h2 className={styles.title}>Sail with these cruise lines</h2>
          <p className={styles.subtitle}>
            Your certificate can be redeemed on sailings from these partners.
          </p>
        </div>

        {logos.length > 0 && (
          <div className={styles.logos}>
            {logos.map((line) => (
              <div key={line.cruiseLineId ?? line.name} className={styles.logoCard}>
                <img
                  src={line.logoUrl}
                  alt={line.name}
                  className={styles.logoImg}
                  loading="lazy"
                />
              </div>
            ))}
          </div>
        )}

        {videos.length > 0 && (
          <div
            className={
              videos.length === 1
                ? `${styles.videos} ${styles.videosOne}`
                : `${styles.videos} ${styles.videosTwo}`
            }
          >
            {videos.map((line) => (
              <CruiseLineVideo key={`${line.cruiseLineId ?? line.name}-video`} line={line} />
            ))}
          </div>
        )}
      </div>
    </section>
  );
}
